import { batch, useSelector, useDispatch } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { ModalProvider } from 'javascripts/components/core/modal/Context';
import { defineMessages, useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import React from 'react';
import styled from 'styled-components';
import { tokens } from '@companycam/slab';

import {
  clearViews,
  setActiveFilterId,
  setCurrentTemplateId,
  setTemplateReorderingType,
} from 'actions/todos';
import {
  defaultActiveFilter,
  templateView,
} from 'components/projects/taskList/constants';

import ListView from './ListView';
import Modal from 'components/shared/DesignSystem/Modal';
import { TemplateModalContext } from '../TemplateModalContext';
import TemplateConfirmView from './TemplateConfirmView';
import TemplateView from './TemplateView';

const messages = defineMessages({
  listHeading: {
    id: 'Projects.taskList.templates.list.heading',
    description: 'Modal heading for templates list view',
    defaultMessage: 'Create a List from Template',
  },
  editHeading: {
    id: 'Projects.taskList.templates.edit.heading',
    description: 'Modal heading for templates edit view',
    defaultMessage: 'Editing Template',
  },
});

const StyledModal = styled(Modal)`
  background-color: white;
  border-radius: ${tokens.size_border_radius_xl}px;
  width: ${({ isConfirmView }) =>
    isConfirmView ? 'min-content' : 'calc(100vw - 64px)'};
  height: ${({ isConfirmView }) =>
    isConfirmView ? 'min-content' : 'calc(100vh - 64px)'};
  max-width: 1200px;
  outline: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  flex: 1;
  padding: 0px;
  h3 {
    font-weight: 600;
    margin: 0;
  }
  h3.modal-title {
    padding: 24px;
    padding-bottom: ${({ isConfirmView }) => (isConfirmView ? '0' : '24px')};
    background-color: ${({ isConfirmView }) =>
      isConfirmView ? 'transparent' : tokens.color_background_2_le};
    margin: 0;
  }
`;

const TemplatesModal = ({
  onRequestClose,
  projectNodeId,
  onRequestBlankTodo,
  projectId,
}) => {
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();
  const currentView = useSelector((state) => state.todos.templates.currentView);
  const currentTemplateId = useSelector(
    (state) => state.todos.templates.currentTemplateId,
  );

  const handleCloseModal = (close) => {
    close();
    batch(() => {
      dispatch(setActiveFilterId(defaultActiveFilter));
      dispatch(setCurrentTemplateId(null));
      dispatch(setTemplateReorderingType(null));
      dispatch(clearViews());
    });
  };

  const renderTemplateView = () => {
    switch (currentView) {
      case templateView.DELETE_TEMPLATE_CONFIRM:
        return <TemplateConfirmView />;
      case templateView.LIST:
        return <ListView />;
      case templateView.EDIT:
        window.Turbo.visit(
          `/todos/templates/edit/${currentTemplateId}?projectId=${projectId}`,
        );
        handleCloseModal(onRequestClose);
        return null;
      case templateView.PREVIEW:
        return (
          <TemplateView
            projectNodeId={projectNodeId}
            onRequestClose={() => handleCloseModal(onRequestClose)}
          />
        );
      default:
        return null;
    }
  };
  const modalTitle = () => {
    switch (currentView) {
      case templateView.EDIT:
        return formatMessage({ id: 'Projects.taskList.templates.edit' });
      case templateView.PREVIEW:
        return formatMessage({ id: 'Projects.taskList.templates.preview' });
      case templateView.LIST:
        return formatMessage(messages.listHeading);
      case templateView.DELETE_TEMPLATE_CONFIRM:
        return formatMessage({ id: 'Projects.taskList.templates.delete' });
      default:
        return '';
    }
  };

  return (
    <BrowserRouter>
      <ModalProvider>
        <TemplateModalContext.Provider
          value={{ projectNodeId, onRequestClose, onRequestBlankTodo }}
        >
          <StyledModal
            isOpen
            isConfirmView={currentView === templateView.DELETE_TEMPLATE_CONFIRM}
            onRequestClose={() => handleCloseModal(onRequestClose)}
            title={modalTitle()}
            className="modal-content-wrap is-padless"
          >
            {renderTemplateView()}
          </StyledModal>
        </TemplateModalContext.Provider>
      </ModalProvider>
    </BrowserRouter>
  );
};

TemplatesModal.propTypes = {
  onRequestClose: PropTypes.func,
  onRequestBlankTodo: PropTypes.func,
  projectNodeId: PropTypes.string,
  projectId: PropTypes.number,
};

export default TemplatesModal;
